import axios from 'axios';
import store from "@/store/BookingStore";
import { Booking, FamilyId, Option, Traveller } from "../../../../requests-module/src";
import { InitBookingRequest } from '@/logic/createRequestParams';
import { BookingState, OptionsChanges } from '@/store/BookingStore.types';
import { isCSE } from '@/utils/cse';
import type { Locale } from '@/service/translate/flights/flightTranslations.types';
import { CSEService } from '../CSEService';
import { log } from 'console';

export class BookingFunnelService {
    private static HEADERS = { 'Content-Type': 'application/json' };

    /**
     * Generic function for initialization API calls using POST.
     * @param alternative - The alternative booking type ('flight', 'transfer', or 'service').
     * @param requestData - The request payload for initialization.
     * @returns A promise with either a Booking or an error.
     */

    private static getBaseUrl() {
        return process.env.VUE_APP_BOOKING_FUNNEL_V2_BASE_URL || window.env.VUE_APP_BOOKING_FUNNEL_V2_BASE_URL
    }

    private static async initApi(
        alternative: 'flight' | 'transfer' | 'service' | 'train',
        requestData: InitBookingRequest
    ): Promise<{ error: string } | Booking> {
        const url = `${this.getBaseUrl()}/init?alternative=${alternative}`;
        const body: InitBookingRequest & { company?: string, companyLang?: string, numeroUsager?: string } = { ...requestData };
        try {
            if (isCSE(window.locale as Locale)) {
                if (store.state.singleCompanyConfiguration?.CompanyName) body.company = store.state.singleCompanyConfiguration?.CompanyName;
                if (store.state.singleCompanyConfiguration?.language) body.companyLang = store.state.singleCompanyConfiguration?.language;
                // take numeroUsager from cookie companyData
                const cseService = new CSEService(window.locale as Locale)
                cseService.init()
                console.log("yyy cseService.companyData", cseService);
                if (cseService.companyData?.numeroUsager) body.numeroUsager = cseService.companyData.numeroUsager;
            }

        } catch (error) {
            console.error(error)
        }

        console.log("yyy body", body);
        try {
            const response = await axios.post<{ error: string } | Booking>(
                url,
                body,
                { headers: this.HEADERS }
            );

            if (!('error' in response.data)) {
                store.commit('setBooking', response.data);
            }

            return response.data;
        } catch (error) {
            console.error(`Error during init (${alternative}) booking:`, error);
            throw error;
        }
    }

    /**
     * Generic function for refresh API calls using GET.
     * When alternative is 'transfer', train options are kept from the current store so a
     * just-saved train selection is not overwritten by a GET that may return stale data.
     * @param alternative - The alternative booking type ('flight', 'transfer', or 'service').
     * @returns A promise with either a Booking or an error.
     */
    private static async refreshApi(
        alternative: 'flight' | 'transfer' | 'service' | 'train'
    ): Promise<{ error: string } | Booking> {
        const token = store.getters.getToken;
        const url = `${this.getBaseUrl()}/${token}?alternative=${alternative}`;
        try {
            const response = await axios.get<{ error: string } | Booking>(
                url,
                { headers: this.HEADERS }
            );

            if (!('error' in response.data)) {
                let dataToSet: Booking = response.data;
                if (alternative === 'transfer') {
                    const current = store.getters.getBooking as Booking | null;
                    const currentOpts = current?.options ?? [];
                    const trainFamilyIds = [FamilyId.TRAIN, FamilyId.TRAIN_SANS_COMPARTIMENT];
                    const trainIds = new Set(
                        currentOpts
                            .filter(o => trainFamilyIds.includes(Number(o.familyId) as FamilyId))
                            .map(o => String(o.id))
                    );
                    if (trainIds.size > 0 && response.data.options?.length) {
                        const mergedOptions: Option[] = response.data.options.map((opt: Option) => {
                            if (trainIds.has(String(opt.id))) {
                                const fromStore = currentOpts.find(o => String(o.id) === String(opt.id));
                                return fromStore ?? opt;
                            }
                            return opt;
                        });
                        dataToSet = { ...response.data, options: mergedOptions };
                    }
                }
                store.commit('setBooking', dataToSet);
            }

            return response.data;
        } catch (error) {
            console.error(`Error during refresh (${alternative}) booking:`, error);
            throw error;
        }
    }

    /**
     * Function to update services using PUT.
     * @param options - An array of Option objects to update.
     * @param alternative - The alternative booking type ('flight', 'transfer', or 'service').
     * @returns A promise with either a Booking or an error.
     */
    static async updateServices(options: Option[], alternative: 'flight' | 'transfer' | 'service' | 'train'): Promise<{ error: string } | Booking> {
        const token = store.getters.getToken;
        const url = `${this.getBaseUrl()}/${token}/services?alternative=${alternative}`;
        try {
            const response = await axios.put<{ error: string } | Booking>(
                url,
                options,
                { headers: this.HEADERS }
            );

            if (!('error' in response.data)) {
                store.commit('setBooking', response.data);
            }

            return response.data;
        } catch (error) {
            const optionNames = options.map(option => option.name || option.id).join(', ');
            console.error(`Error during update services for options: [${optionNames}]:`, error);
            throw error;
        }
    }

    /**
 * Updates the participants for a booking with alternative = service.
 * @param participants - The array of participant objects to update.
 * @returns A promise with either a Booking or an error.
 */
    static async updateParticipants(participants: Traveller[], validate?: boolean): Promise<{ success: boolean, booking?: Booking, error?: string, changes?: OptionsChanges[], travellers: { before: Traveller[], after: Traveller[] } }> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');
        console.log("[updateParticipants] participants:", participants);
        const url = `${this.getBaseUrl()}/${token}/participants?alternative=service${validate ? '&validate=true' : ''}`;
        try {
            const response = await axios.put<{ success: boolean, booking?: Booking, error?: string, changes?: OptionsChanges[], travellers: { before: Traveller[], after: Traveller[] } }>(
                url,
                participants,
                { headers: this.HEADERS }
            );


            if (!('error' in response.data)) {
                if (response.data.changes) {
                    store.commit('setOptionsChanges', { changes: response.data.changes, travellers: response.data.travellers });
                }
                if (response.data.booking) {
                    store.commit('setBooking', response.data.booking)
                }
            }
            return response.data
        } catch (error) {
            console.error('Error updating participants (service):', error);
            throw error;
        }
    }

    static async deleteParticipant(participantId: string): Promise<{ success: boolean, booking?: Booking, error?: string }> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');
        console.log("[deleteParticipant]");
        const url = `${this.getBaseUrl()}/${token}/participants/${participantId}`;
        try {
            const response = await axios.delete<{ success: boolean, booking?: Booking, error?: string }>(url, { headers: this.HEADERS });
            if (!('error' in response.data)) {
                if (response.data.booking) {
                    store.commit('setBooking', response.data.booking)
                }
            }
            return response.data
        }
        catch (error) {
            console.error('Error deleting participant:', error);
            throw error;
        }
    }

    static async addParticipant(participant: Traveller): Promise<{ success: boolean, booking?: Booking, error?: string }> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');
        console.log("[addParticipant]");
        const url = `${this.getBaseUrl()}/${token}/participants`;
        try {
            const response = await axios.post<{ success: boolean, booking?: Booking, error?: string }>(
                url,
                participant,
                { headers: this.HEADERS }
            );
            if (!('error' in response.data)) {
                if (response.data.booking) {
                    store.commit('setBooking', response.data.booking)
                }
            }
            return response.data
        } catch (error) {
            console.error('Error updating participants (service):', error);
            throw error;
        }
    }


    static async setContact(email: string): Promise<{ error: string } | Booking> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');
        const url = `${this.getBaseUrl()}/${token}/save-contact`;
        try {
            const response = await axios.post<{ error: string } | Booking>(url, { email }, { headers: this.HEADERS });
            return response.data;
        } catch (error) {
            console.error('Error setting contact:', error);
            throw error;
        }
    }

    static async saveState(state: Booking): Promise<{ error: string } | Booking> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');
        const url = `${this.getBaseUrl()}/${token}/update-state`;
        try {
            const response = await axios.put<{ error: string } | Booking>(url, state, { headers: this.HEADERS });
            return response.data;
        } catch (error) {
            console.error('Error saving state:', error);
            throw error;
        }
    }

    // Flight-specific functions
    static async initBookingFlight(requestData: InitBookingRequest): Promise<{ error: string } | Booking> {
        console.log('Init booking + flight call');
        return this.initApi('flight', requestData);
    }

    static async refreshBookingFlight(): Promise<{ error: string } | Booking> {
        console.log('Refresh booking + flight call');
        return this.refreshApi('flight');
    }

    static async initBookingTrain(requestData: InitBookingRequest): Promise<{ error: string } | Booking> {
        console.log('Init booking + train call');
        return this.initApi('train', requestData);
    }

    static async refreshBookingTrain(): Promise<{ error: string } | Booking> {
        console.log('Refresh booking + train call');
        return this.refreshApi('train');
    }

    // Transfer-specific functions
    static async initBookingTransfer(requestData: InitBookingRequest): Promise<{ error: string } | Booking> {
        console.log('Init booking + transfer call');
        return this.initApi('transfer', requestData);
    }

    static async refreshBookingTransfer(): Promise<{ error: string } | Booking> {
        console.log('Refresh booking + transfer call');
        return this.refreshApi('transfer');
    }

    // Options-specific functions
    static async initBookingOptions(requestData: InitBookingRequest): Promise<{ error: string } | Booking> {
        console.log('Init booking + options call');
        return this.initApi('service', requestData);
    }

    static async refreshBookingOptions(): Promise<{ error: string } | Booking> {
        console.log('Refresh booking + options call');
        return this.refreshApi('service');
    }

    /**
   * APPLY PROMOCODE
   * @param promocode - the promotional code to apply
   * @returns updated Booking or { error: string }
   */
    public static async applyPromoCode(
        promocode: string,
    ): Promise<{ error: string } | Booking> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');

        const url = `${this.getBaseUrl()}/${token}/promocode?alternative=service`;
        try {
            const response = await axios.post<{ error: string } | Booking>(
                url,
                { promocode },
                { headers: this.HEADERS }
            );

            if (!('error' in response.data)) {
                store.commit('setBooking', response.data);
            }

            return response.data;
        } catch (error) {
            console.error(`Error applying promo code:`, error);
            throw error;
        }
    }

    /**
   * APPLY PROMOCODE V2
   * @param promocode - the promotional code to apply
   * @param email - optional email address (required for some promocodes)
   * @param externalValidationData - optional external validation data (for partner validation)
   * @returns updated Booking or { error: string, needsEmail?: boolean, externalValidationRequired?: boolean, externalValidationProvider?: string, externalValidationFields?: any[] }
   */
    public static async applyPromocodeV2(
        promocode: string,
        email?: string,
        externalValidationData?: { [key: string]: any } | null
    ): Promise<{ error: string; needsEmail?: boolean; externalValidationRequired?: boolean; externalValidationProvider?: string; externalValidationFields?: any[] } | Booking> {
        const token = store.getters.getToken;
        if (!token) throw new Error('No booking token in state');

        const normalizedPromocode = promocode.trim() === '' ? promocode : promocode.trim().toUpperCase();
        const url = `${this.getBaseUrl()}/${token}/promocode-v2`;
        try {
            const response = await axios.post<{ error: string; needsEmail?: boolean; externalValidationRequired?: boolean; externalValidationProvider?: string; externalValidationFields?: any[] } | Booking>(
                url,
                { promocode: normalizedPromocode, email, externalValidationData },
                { headers: this.HEADERS }
            );

            if (!('error' in response.data)) {
                store.commit('setBooking', response.data);
            }

            return response.data;
        } catch (error: any) {
            console.error(`Error applying promo code V2:`, error);
            // Try to extract error from response
            if (error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    }


}
